import React, { ReactNode, useState } from 'react';
import css from './index.module.css';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
} from '../../components/common/tabs/tab-container';
import TodoFeature from '../../components/common/todo-feature';
import { LoadingWrapper } from '../../components/common/loading-wrapper';
import ModalShowcase from './modals';

export default function DevelopmentSandbox() {
  return (
    <div className="container mx-auto">
      <h1 className={css['title']}>Component Sandbox</h1>
      <p className={css['summary']}>
        This page contains examples of how to use various components in the app.
        <br />
        It is available only in development mode and can be used as a quick
        reference.
      </p>

      {/* Add more isolated functional components with examples of all of the components in the app */}
      <TabShowcase />
      <ModalShowcase />
    </div>
  );
}

function TabShowcase() {
  return (
    <LayoutRow>
      <ComponentShowcase
        title="Tabs"
        shortDescription="Component for the display of tabs in the application. Lazy loading is handled by the children components."
      >
        <ComponentShowcaseTitle title="Tabs with lazy loading" />
        <TabLazyLoadingExample />

        <ComponentShowcaseTitle title="Lots of tabs" />
        <TabManyTabsExample />

        <ComponentShowcaseTitle title="Changing tabs programmatically" />
        <TabProgrammaticExample />

        <ComponentShowcaseTitle title="Storing tab in the URL" />
        <TodoFeature name="This is not currently implemented, but would be a nice to have." />
      </ComponentShowcase>
    </LayoutRow>
  );
}

enum InitializationType {
  DIRECT,
  WEB_REQUEST,
  WEB_REQUEST_THAT_WILL_FAIL,
}

function LazyLoadingExampleComponent({
  name,
  initializationType,
}: {
  name: String;
  initializationType: InitializationType;
}) {
  const [dataLoaded, setLoadedData] = useState(null);
  let simulatedLocalDataPreparationDelay = Math.random() * 250 + 250; // under a second
  let simulatedNetworkDelay = Math.random() * 1000 + 2000; // just a few seconds
  let onLoadMethod: Function;
  switch (initializationType) {
    case InitializationType.DIRECT: // when we want to simulate initializing state from data we already have loaded
      onLoadMethod = () => {
        setTimeout(() => {
          const newDate = new Date().toISOString();
          setLoadedData(newDate);
        }, simulatedLocalDataPreparationDelay);
      };
      break;
    case InitializationType.WEB_REQUEST: // when we want to simulate some data being initialized from REST or elsewhere
      onLoadMethod = () => {
        console.log('Simulating fetch...');
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            const newDate = new Date().toISOString();
            setLoadedData(newDate);
            console.log('Assuming fetch succeeded!');
            resolve(newDate);
          }, simulatedNetworkDelay);
        });
      };
      break;
    case InitializationType.WEB_REQUEST_THAT_WILL_FAIL: // when we want to simulate failed data initialization from REST or elsewhere
      onLoadMethod = () => {
        console.log('Simulating fetch that will fail...');
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            setLoadedData(null);
            console.log('Assuming fetch failed!!');
            reject('Simulated failure!');
          }, simulatedNetworkDelay);
        });
      };
      break;
  }
  return (
    <LoadingWrapper onLoad={onLoadMethod}>
      Lazy loading example component: {name}
      <br />
      The component's data was loaded on: {dataLoaded}
    </LoadingWrapper>
  );
}

/** After a few discussions, it was decided that it'd be best if the individual components did handle the lazy loading by themselves. **/
function TabLazyLoadingExample() {
  const [currentTab, setTab] = useState('test-2');
  return (
    <TabContainer
      selectedTabKey={currentTab}
      setTab={setTab}
      tabNavigation={
        <TabNavigation>
          <TabLink tabKey="test-1">Test Tab #1</TabLink>
          <TabLink tabKey="test-2">Test Tab #2 (default)</TabLink>
          <TabLink tabKey="test-3" disabled={true}>
            Test Tab #3 (disabled)
          </TabLink>
          <TabLink tabKey="test-4">Test Tab #4 (loads slowly)</TabLink>
          <TabLink tabKey="test-5">Test Tab #5 (loads slowly)</TabLink>
          <TabLink tabKey="test-6">Test Tab #6 (that fails to load)</TabLink>
        </TabNavigation>
      }
      tabContents={
        <TabContents>
          <Tab tabKey="test-1">
            <LazyLoadingExampleComponent
              name="Test tab #1"
              initializationType={InitializationType.DIRECT}
            />
          </Tab>
          <Tab tabKey="test-2">
            <LazyLoadingExampleComponent
              name="Test tab #2"
              initializationType={InitializationType.DIRECT}
            />
          </Tab>
          <Tab tabKey="test-3">
            <LazyLoadingExampleComponent
              name="Test tab #3"
              initializationType={InitializationType.DIRECT}
            />
          </Tab>
          <Tab tabKey="test-4">
            <LazyLoadingExampleComponent
              name="Test tab #4"
              initializationType={InitializationType.WEB_REQUEST}
            />
          </Tab>
          <Tab tabKey="test-5">
            <LazyLoadingExampleComponent
              name="Test tab #5"
              initializationType={InitializationType.WEB_REQUEST}
            />
          </Tab>
          <Tab tabKey="test-6">
            <LazyLoadingExampleComponent
              name="Test tab #6"
              initializationType={InitializationType.WEB_REQUEST_THAT_WILL_FAIL}
            />
          </Tab>
        </TabContents>
      }
    />
  );
}

function TabManyTabsExample() {
  const [currentTab, setTab] = useState('tab-1');
  return (
    <TabContainer
      selectedTabKey={currentTab}
      setTab={setTab}
      tabNavigation={
        <TabNavigation>
          <TabLink tabKey={'tab-1'}>Test tab #1 link</TabLink>
          <TabLink tabKey={'tab-2'}>Test tab #2 link</TabLink>
          <TabLink tabKey={'tab-3'}>Test tab #3 link</TabLink>
          <TabLink tabKey={'tab-4'}>Test tab #4 link</TabLink>
          <TabLink tabKey={'tab-5'}>Test tab #5 link</TabLink>
          <TabLink tabKey={'tab-6'}>Test tab #6 link</TabLink>
          <TabLink tabKey={'tab-7'}>Test tab #7 link</TabLink>
          <TabLink tabKey={'tab-8'}>Test tab #8 link</TabLink>
        </TabNavigation>
      }
      tabContents={
        <TabContents>
          <Tab tabKey="tab-1">This is tab #1</Tab>
          <Tab tabKey="tab-2">This is tab #2</Tab>
          <Tab tabKey="tab-3">This is tab #3</Tab>
          <Tab tabKey="tab-4">This is tab #4</Tab>
          <Tab tabKey="tab-5">This is tab #5</Tab>
          <Tab tabKey="tab-6">This is tab #6</Tab>
          <Tab tabKey="tab-7">This is tab #7</Tab>
          <Tab tabKey="tab-8">This is tab #8</Tab>
        </TabContents>
      }
    />
  );
}

function TabProgrammaticExample() {
  const [currentTab, setTab] = useState('test-1');
  return (
    <div>
      <TabContainer
        selectedTabKey={currentTab}
        setTab={setTab}
        tabNavigation={
          <TabNavigation>
            <TabLink tabKey="test-1">Test Tab #1</TabLink>
            <TabLink tabKey="test-2">Test Tab #2</TabLink>
          </TabNavigation>
        }
        tabContents={
          <TabContents>
            <Tab tabKey="test-1">This is test tab #1</Tab>
            <Tab tabKey="test-2">This is test tab #2</Tab>
          </TabContents>
        }
      />
      <div className={css['button-container']}>
        <button onClick={() => setTab('test-1')}>Open Tab #1</button>
        <button onClick={() => setTab('test-2')}>Open Tab #2</button>
      </div>
    </div>
  );
}

export function ComponentShowcase({
  title,
  shortDescription,
  children,
}: {
  title: String;
  shortDescription: String;
  children?: ReactNode;
}) {
  return (
    <LayoutRow>
      <LayoutRow>
        <h2 className={css['component-showcase-title']}>{title}</h2>
        <p>{shortDescription}</p>
      </LayoutRow>
      <LayoutRow>{children}</LayoutRow>
    </LayoutRow>
  );
}

export function ComponentShowcaseTitle({ title }: { title: String }) {
  return (
    <LayoutRow>
      <h3 className="underline">{title}</h3>
    </LayoutRow>
  );
}

export function LayoutRow({ children }: { children?: ReactNode }) {
  return <div className={css['row']}>{children}</div>;
}
